import React, {
  useCallback,
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type Ref,
  type RefCallback,
} from "react";

/** Applies React `CSSProperties` to a DOM node (including `--*` custom properties). */
export function applyTemplateStyles(
  el: HTMLElement | null | undefined,
  style: CSSProperties | undefined,
): void {
  if (!el || !style) return;
  for (const [key, value] of Object.entries(style)) {
    if (value === undefined || value === null) continue;
    if (key.startsWith("--")) {
      el.style.setProperty(key, String(value));
    } else {
      (el.style as unknown as Record<string, string>)[key] = String(value);
    }
  }
}

export function mergeTemplateRefs<T>(
  ...refs: Array<Ref<T> | undefined>
): RefCallback<T> {
  return (value: T | null) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === "function") ref(value);
      else (ref as React.MutableRefObject<T | null>).current = value;
    }
  };
}

/**
 * Binds dynamic template styles on a host element without a JSX `style` prop.
 * Use for theme tokens, section-specific CSS variables, and layout overrides.
 */
export function useTemplateStyles<T extends HTMLElement>(
  style: CSSProperties | undefined,
): RefCallback<T> {
  const styleRef = useRef(style);
  styleRef.current = style;

  const nodeRef = useRef<T | null>(null);

  useLayoutEffect(() => {
    applyTemplateStyles(nodeRef.current, styleRef.current);
  }, [style]);

  return useCallback((node: T | null) => {
    nodeRef.current = node;
    applyTemplateStyles(node, styleRef.current);
  }, []);
}
